import React, { useEffect, useState, useRef } from "react";
import {
    Box,
    Typography,
    Paper,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Snackbar,
    Alert,
} from "@mui/material";
import { FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import api from "../../api";

import { Cropper, RectangleStencil } from "react-advanced-cropper";
import "react-advanced-cropper/dist/style.css";

export default function Schools() {
    const [loading, setLoading] = useState(true);
    const [schools, setSchools] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [formData, setFormData] = useState({
        id: "",
        name: "",
        code: "",
        address: "",
        phone: "",
        email: "",
        domain: "",
        subscription_amount: "",
        logo: null,
    });
    const [editId, setEditId] = useState(null);

    const [image, setImage] = useState(null);
    const [croppedImg, setCroppedImg] = useState(null);
    const [oldLogo, setOldLogo] = useState(null);
    const cropperRef = useRef(null);

    // Snackbar state
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });

    const fetchSchools = async () => {
        setLoading(true);
        try {
            const res = await api.get("/super_admin/school/list");
            setSchools(res.data?.data || []);
        } catch (err) {
            console.error("Fetch schools error:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSchools();
    }, []);

    const handleOpen = (school = null) => {
        if (school) {
            setFormData({
                id: school.id,
                name: school.name,
                code: school.code,
                address: school.address || "",
                phone: school.phone || "",
                email: school.email || "",
                domain: school.domain || "",
                subscription_amount: school.subscription_amount || "",
                logo: null,
            });
            setEditId(school.id);
            setCroppedImg(null);
            setOldLogo(
                school.logo
                    ? `https://dgps.co.in/public/admin/assets/img/schools/logo/${school.logo}`
                    : null
            );
        } else {
            setFormData({
                id: "",
                name: "",
                code: "",
                address: "",
                phone: "",
                email: "",
                domain: "",
                subscription_amount: "",
                logo: null,
            });
            setEditId(null);
            setCroppedImg(null);
            setOldLogo(null);
        }
        setOpenDialog(true);
    };

    const handleClose = () => {
        setOpenDialog(false);
        setFormData({
            id: "",
            name: "",
            code: "",
            address: "",
            phone: "",
            email: "",
            domain: "",
            subscription_amount: "",
            logo: null,
        });
        setEditId(null);
        setCroppedImg(null);
        setOldLogo(null);
    };

    const handleSave = async () => {
        try {
            const fd = new FormData();
            Object.keys(formData).forEach((key) => {
                if (formData[key] !== null && formData[key] !== "") {
                    fd.append(key, formData[key]);
                }
            });

            let res;
            if (editId) {
                res = await api.post("/super_admin/school/update", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            } else {
                res = await api.post("/super_admin/school/add", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            }

            setSnackbar({ open: true, message: res.data?.message || "Success", severity: "success" });

            handleClose();
            fetchSchools();
        } catch (err) {
            console.error("Save error:", err);
            setSnackbar({
                open: true,
                message: err.response?.data?.message || "Error occurred",
                severity: "error",
            });
        }
    };

    const confirmDelete = (id) => {
        setDeleteConfirm({ open: true, id });
    };

    const handleDelete = async () => {
        try {
            const fd = new FormData();
            fd.append("id", deleteConfirm.id);

            const res = await api.post("/super_admin/school/delete", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            setSnackbar({ open: true, message: res.data?.message || "Deleted", severity: "success" });
            setDeleteConfirm({ open: false, id: null });
            fetchSchools();
        } catch (err) {
            console.error("Delete error:", err);
            setSnackbar({
                open: true,
                message: err.response?.data?.message || "Delete failed",
                severity: "error",
            });
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            if (file.type !== "image/png") {
                alert("Only PNG images are allowed!");
                e.target.value = "";
                return;
            }
            const reader = new FileReader();
            reader.onload = () => {
                setImage(reader.result);
                setCroppedImg(null);
                setOldLogo(null);
            };
            reader.readAsDataURL(file);
        }
    };

    const doCrop = () => {
        if (cropperRef.current) {
            const canvas = cropperRef.current.getCanvas();
            if (canvas) {
                canvas.toBlob((blob) => {
                    if (blob) {
                        const file = new File([blob], "logo.png", { type: "image/png" });
                        setFormData((prev) => ({ ...prev, logo: file }));
                        setCroppedImg(URL.createObjectURL(blob));
                        setImage(null);
                        setOldLogo(null);
                    }
                }, "image/png");
            }
        }
    };

    return (
        <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                <Typography variant="h5" fontWeight={800}>
                    Schools Management
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<FaPlus />}
                    onClick={() => handleOpen()}
                >
                    Add School
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Logo</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Code</TableCell>
                                <TableCell>Phone</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Domain</TableCell>
                                <TableCell>Created At</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {schools.map((school) => (
                                <TableRow key={school.id}>
                                    <TableCell>{school.id}</TableCell>
                                    <TableCell>
                                        {school.logo ? (
                                            <img
                                                src={`https://dgps.co.in/public/admin/assets/img/schools/logo/${school.logo}`}
                                                alt="Logo"
                                                style={{ width: "40px", height: "40px", borderRadius: "6px" }}
                                            />
                                        ) : (
                                            "-"
                                        )}
                                    </TableCell>
                                    <TableCell>{school.name}</TableCell>
                                    <TableCell>{school.code}</TableCell>
                                    <TableCell>{school.phone}</TableCell>
                                    <TableCell>{school.email}</TableCell>
                                    <TableCell>{school.domain}</TableCell>
                                    <TableCell>{school.created_at}</TableCell>
                                    <TableCell align="right">
                                        <Button
                                            size="small"
                                            startIcon={<FaEdit />}
                                            onClick={() => handleOpen(school)}
                                            sx={{ mr: 1 }}
                                        >
                                            Edit
                                        </Button>
                                        <Button
                                            size="small"
                                            color="error"
                                            startIcon={<FaTrash />}
                                            onClick={() => confirmDelete(school.id)}
                                        >
                                            Delete
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {!schools.length && (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        No schools found
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Add/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>{editId ? "Edit School" : "Add School"}</DialogTitle>
                <DialogContent>
                    <TextField margin="dense" label="School Name" fullWidth value={formData.name}
                        onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))} />
                    <TextField margin="dense" label="School Code" fullWidth value={formData.code}
                        onChange={(e) => setFormData((f) => ({ ...f, code: e.target.value }))} />
                    <TextField margin="dense" label="Address" fullWidth value={formData.address}
                        onChange={(e) => setFormData((f) => ({ ...f, address: e.target.value }))} />
                    <TextField margin="dense" label="Phone" fullWidth value={formData.phone}
                        onChange={(e) => setFormData((f) => ({ ...f, phone: e.target.value }))} />
                    <TextField margin="dense" label="Email" fullWidth value={formData.email}
                        onChange={(e) => setFormData((f) => ({ ...f, email: e.target.value }))} />
                    <TextField margin="dense" label="Domain" fullWidth value={formData.domain}
                        onChange={(e) => setFormData((f) => ({ ...f, domain: e.target.value }))} />
                    <TextField margin="dense" label="Subscription Amount" type="number" fullWidth
                        value={formData.subscription_amount}
                        onChange={(e) => setFormData((f) => ({ ...f, subscription_amount: e.target.value }))} />

                    {/* Logo */}
                    <Box mt={2}>
                        <Typography variant="body2" sx={{ mb: 1 }}>Logo</Typography>
                        {oldLogo && !image && !croppedImg && (
                            <Box>
                                <img src={oldLogo} alt="Old Logo" style={{ width: "100px", borderRadius: "6px" }} />
                                <Box mt={1}>
                                    <Button size="small" onClick={() => setOldLogo(null)}>Change Logo</Button>
                                </Box>
                            </Box>
                        )}
                        {!image && !croppedImg && !oldLogo && (
                            <input type="file" accept="image/png" onChange={handleFileChange} />
                        )}
                        {image && !croppedImg && (
                            <Box sx={{ mt: 2 }}>
                                <Cropper ref={cropperRef} src={image} stencilComponent={RectangleStencil}
                                    className="cropper" style={{ height: 200, width: "100%", borderRadius: "6px" }} />
                                <Box mt={2} sx={{ display: "flex", gap: 2 }}>
                                    <Button onClick={() => setImage(null)}>Cancel</Button>
                                    <Button variant="contained" onClick={doCrop}>Crop & Save</Button>
                                </Box>
                            </Box>
                        )}
                        {croppedImg && (
                            <Box mt={2}>
                                <img src={croppedImg} alt="Cropped Logo" style={{ width: "100px", borderRadius: "6px" }} />
                                <Box mt={1}>
                                    <Button size="small" onClick={() => { setImage(null); setCroppedImg(null); setFormData((f) => ({ ...f, logo: null })); }}>
                                        Change Logo
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained">
                        {editId ? "Update" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, id: null })}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>Are you sure you want to delete this school?</DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm({ open: false, id: null })}>Cancel</Button>
                    <Button color="error" onClick={handleDelete}>Delete</Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "top", horizontal: "center" }} // ✅ Center Top
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

        </Box>
    );
}
